/* Parsing a door's answer, at the boundary where the claim about it is made.
 *
 * `doorClient<T>(path)` used to end in `return (await resp.json()) as T`. That cast is the
 * only thing standing between a generated type and the wire, and it proves nothing: the
 * types in `schema.gen.ts` are erased at build, and the service on the other end is a
 * separately promoted image that may be running a different revision of this repo. When a
 * field the type declares required arrives absent, nothing fails at the boundary — it fails
 * later, as `Cannot read properties of undefined (reading 'some')`, inside a render, under
 * a component name that has nothing to do with the door that lied.
 *
 * These are *structural* decoders, and the distinction is load-bearing: they prove the
 * object-and-list skeleton a call site walks and the individual primitive leaves a caller
 * explicitly asks them to check. They are not whole-schema validation. Complete per-field
 * validation belongs to validators generated from the same OpenAPI documents
 * `schema.gen.ts` is generated from; that does not exist yet. Treating a handful of checks
 * as if it did would be worse than the cast they replace, because it would be a claim rather
 * than a gap.
 */

/** A decoder turns an untrusted JSON value into a typed one, or throws `DecodeError`.
 *
 * `source` is the exact successful JSON response when a boundary needs lexical JSON
 * identity in addition to the ordinary parsed value. Most decoders correctly ignore it.
 */
export type Decoder<T> = (raw: unknown, source?: string) => T;

/** Raised by a decoder; `doorClient` converts it into an `ApiError` carrying the door and
 * path, so a malformed response refuses like any other bad response rather than escaping
 * as a `TypeError` half a component tree away. */
export class DecodeError extends Error {
  constructor(
    /** The dotted path of what was being read, e.g. `session.principal.grants`. */
    public readonly what: string,
    message: string,
  ) {
    super(message);
    this.name = "DecodeError";
  }
}

/** Narrow to a JSON object. Arrays are objects to `typeof` and are rejected here: a caller
 * asking for a record and handed a list is exactly the confusion worth catching. */
export function expectRecord(
  raw: unknown,
  what: string,
): Record<string, unknown> {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    throw new DecodeError(what, `${what} is not an object`);
  }
  return raw as Record<string, unknown>;
}

/** Narrow to a JSON list. */
export function expectArray(raw: unknown, what: string): readonly unknown[] {
  if (!Array.isArray(raw)) {
    throw new DecodeError(what, `${what} is not a list`);
  }
  return raw;
}

/** Narrow a required string leaf before code calls string methods on it. */
export function expectString(raw: unknown, what: string): string {
  if (typeof raw !== "string") {
    throw new DecodeError(what, `${what} is not a string`);
  }
  return raw;
}

/** Narrow a required numeric leaf before code formats or compares it. */
export function expectNumber(raw: unknown, what: string): number {
  if (typeof raw !== "number" || !Number.isFinite(raw)) {
    throw new DecodeError(what, `${what} is not a finite number`);
  }
  return raw;
}

/* The four primitives above are what a decoder is *made of*. What a call site actually
 * writes, over and over, is "this response is a list of records, and these fields of each
 * record are the ones my screen walks" — so the combinators for that live here too, beside
 * the primitives they compose, rather than being re-derived per workspace. They were, for a
 * while: Library and Training each carried their own `RecordShape` + `arrayResponse`, and
 * Capture's absence of a third copy is what let a door answering `{}` reach a render. */

/** The fields of a record whose *kind* a caller depends on. Every list is optional and
 * checked only if given: a decoder proves what its screen walks, not the whole schema. */
export interface RecordShape {
  readonly arrays?: readonly string[];
  readonly numbers?: readonly string[];
  readonly records?: readonly string[];
  readonly strings?: readonly string[];
}

/** Narrow to a record and check the fields `shape` names. */
export function expectShape(
  raw: unknown,
  what: string,
  shape: RecordShape,
): Record<string, unknown> {
  const value = expectRecord(raw, what);
  shape.arrays?.forEach((field) =>
    expectArray(value[field], `${what}.${field}`),
  );
  shape.numbers?.forEach((field) =>
    expectNumber(value[field], `${what}.${field}`),
  );
  shape.records?.forEach((field) =>
    expectRecord(value[field], `${what}.${field}`),
  );
  shape.strings?.forEach((field) =>
    expectString(value[field], `${what}.${field}`),
  );
  return value;
}

/** Narrow to a list and check every element against `shape`. */
export function expectRecordArray(
  raw: unknown,
  what: string,
  shape: RecordShape,
): readonly unknown[] {
  const rows = expectArray(raw, what);
  rows.forEach((item, index) => expectShape(item, `${what}[${index}]`, shape));
  return rows;
}

/** A decoder for a response that is a list of records. */
export const arrayResponse =
  <T>(what: string, shape: RecordShape = {}): Decoder<T[]> =>
  (raw) => {
    expectRecordArray(raw, what, shape);
    return raw as T[];
  };

/** A decoder for a response that is one record. `itemShapes` reaches one level further, into
 * the elements of a field `shape.arrays` already proved is a list. */
export const recordResponse =
  <T>(
    what: string,
    shape: RecordShape = {},
    itemShapes: Readonly<Record<string, RecordShape>> = {},
  ): Decoder<T> =>
  (raw) => {
    const value = expectShape(raw, what, shape);
    for (const [field, itemShape] of Object.entries(itemShapes)) {
      expectRecordArray(value[field], `${what}.${field}`, itemShape);
    }
    return raw as T;
  };
